import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, isSupabaseConnected } from "@/lib/supabase";

const ADMIN_ACCOUNTS = [
  { email: "mohit@expertadvise.in", password: "123456", name: "Mohit Admin" },
  { email: "raj@expertadvise.in", password: "123456", name: "Raj Admin" },
];

export default function AdminSetupPage() {
  const [status, setStatus] = useState<string[]>(["Initializing setup..."]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSupabaseConnected || !supabase) {
      setError("Supabase is not connected. Cannot create admin accounts.");
      return;
    }

    async function setupAdmins() {
      for (const account of ADMIN_ACCOUNTS) {
        try {
          setStatus((prev) => [...prev, `Creating account for ${account.email}...`]);

          // Step 1: Sign up the user
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: account.email,
            password: account.password,
            options: { data: { name: account.name } },
          });

          if (signUpError) {
            // If user already exists, try sign in to get user ID
            if (signUpError.message.includes("already registered") || signUpError.message.includes("already exists")) {
              const { data: signInData } = await supabase.auth.signInWithPassword({
                email: account.email,
                password: account.password,
              });
              if (signInData?.user) {
                setStatus((prev) => [...prev, `  Account already exists, using existing: ${account.email}`]);
                // Update role to admin
                await supabase
                  .from("profiles")
                  .update({ role: "admin", name: account.name })
                  .eq("id", signInData.user.id);
                setStatus((prev) => [...prev, `  Updated role to admin: ${account.email}`]);
                await supabase.auth.signOut();
                continue;
              }
            }
            setStatus((prev) => [...prev, `  ERROR for ${account.email}: ${signUpError.message}`]);
            continue;
          }

          if (signUpData?.user) {
            setStatus((prev) => [...prev, `  Created auth user: ${signUpData.user.id}`]);

            // Step 2: Update profile to admin
            const { error: updateError } = await supabase
              .from("profiles")
              .update({ role: "admin", name: account.name })
              .eq("id", signUpData.user.id);

            if (updateError) {
              setStatus((prev) => [...prev, `  ERROR updating role: ${updateError.message}`]);
            } else {
              setStatus((prev) => [...prev, `  Role updated to admin: ${account.email}`]);
            }

            // Sign out so next signup works
            await supabase.auth.signOut();
          }
        } catch (err) {
          setStatus((prev) => [...prev, `  EXCEPTION for ${account.email}: ${String(err)}`]);
        }
      }

      setDone(true);
      setStatus((prev) => [...prev, "Setup complete! Redirecting to login..."]);
      setTimeout(() => navigate("/login"), 3000);
    }

    setupAdmins();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900">Admin Account Setup</h1>
          <p className="text-sm text-slate-500 mt-1">Creating admin accounts...</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-200 space-y-1 max-h-64 overflow-y-auto">
          {status.map((s, i) => (
            <div key={i} className={s.includes("ERROR") ? "text-red-400" : s.includes("complete") ? "text-emerald-400" : ""}>
              {s}
            </div>
          ))}
          {!done && (
            <div className="text-slate-500 animate-pulse">Processing...</div>
          )}
        </div>

        {done && (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/login")}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}